// src/components/TiendanubeRedirect.js
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';

/**
 * Componente que maneja y procesa la redirección OAuth de Tiendanube
 * Este componente es crítico para manejar el caso donde el usuario llega a /api/auth/callback
 * en el frontend cuando debería estar siendo manejado por el backend
 */
function TiendanubeRedirect() {
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [redirecting, setRedirecting] = useState(true);
  const [redirectAttempts, setRedirectAttempts] = useState(0);
  const navigate = useNavigate();
  
  // Función auxiliar para detectar bucles de redirección
  const detectRedirectLoop = () => {
    // Obtener el historial de redirecciones almacenado
    const redirectHistory = JSON.parse(sessionStorage.getItem('redirectHistory') || '[]');
    const currentPath = window.location.pathname + window.location.search;
    const timestamp = new Date().getTime();
    
    // Si hay más de 3 redirecciones a la misma ruta en menos de 10 segundos, es un bucle
    const recentRedirects = redirectHistory.filter(item => 
      item.path === currentPath && 
      timestamp - item.time < 10000
    );
    
    // Actualizar el historial
    redirectHistory.push({ path: currentPath, time: timestamp });
    // Mantener solo las últimas 10 entradas
    while (redirectHistory.length > 10) redirectHistory.shift();
    sessionStorage.setItem('redirectHistory', JSON.stringify(redirectHistory));
    
    return recentRedirects.length >= 2; // Detectamos un bucle si la misma ruta aparece 3+ veces
  };

  useEffect(() => {
    // Primero verificamos si hay un bucle de redirección
    const isInRedirectLoop = detectRedirectLoop();
    if (isInRedirectLoop) {
      console.error('[TiendanubeRedirect] BUCLE DE REDIRECCIÓN DETECTADO - Deteniendo el ciclo');
      sessionStorage.setItem('redirectLoopDetected', 'true');
      sessionStorage.setItem('redirectLoopTime', new Date().toISOString());
      
      setErrorMessage('Se detectó un bucle de redirección. Por favor, intente nuevamente desde la página principal.');
      setRedirecting(false);
      return; // Detenemos la ejecución para romper el bucle
    }
    
    // Verificar si ya hay un usuario autenticado
    if (auth.currentUser) {
      console.log('[TiendanubeRedirect] Usuario ya autenticado:', auth.currentUser.uid);
      navigate('/', { replace: true });
      return;
    }

    // Registrar el referrer para detectar si venimos de login de Tiendanube
    const referrer = document.referrer;
    const isFromTiendanubeLogin = referrer && referrer.includes('tiendanube.com/login');
    
    // Detectar si tenemos un código de autorización en la URL
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    
    // Incrementar contador de intentos de redirección
    const attempts = redirectAttempts + 1;
    setRedirectAttempts(attempts);
    
    // Guardar datos para diagnóstico en sessionStorage
    sessionStorage.setItem('lastRedirectTime', new Date().toISOString());
    sessionStorage.setItem('lastRedirectPath', window.location.pathname);
    sessionStorage.setItem('lastReferrer', referrer || 'no-referrer');
    sessionStorage.setItem('redirectAttempts', attempts.toString());
    
    // Log detallado de la ubicación actual para diagnóstico
    console.log('[TiendanubeRedirect] URL completa:', window.location.href);
    console.log('[TiendanubeRedirect] Pathname:', window.location.pathname);
    console.log('[TiendanubeRedirect] Referrer:', referrer);
    console.log('[TiendanubeRedirect] Search params:', Object.fromEntries(searchParams.entries()));
    
    // Revisar si hubo un error en la respuesta de OAuth
    if (error) {
      console.error(`[TiendanubeRedirect] Error OAuth: ${error}`);
      sessionStorage.setItem('oauthError', error);
      sessionStorage.setItem('oauthErrorTime', new Date().toISOString());
      
      setErrorMessage(`Error de autorización: ${error}. Por favor intente nuevamente.`);
      setRedirecting(false);
      
      // Si hay demasiados intentos, simplemente vamos al inicio
      if (attempts > 3) {
        console.warn('[TiendanubeRedirect] Demasiados intentos de redirección. Enviando al inicio.');
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      } else {
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
      return;
    }
    
    if (code) {
      // Si tenemos un código, necesitamos asegurarnos que sea procesado por el backend
      console.log('[TiendanubeRedirect] Código de autorización detectado en URL frontend.');
      sessionStorage.setItem('oauthCodeReceived', 'true');
      sessionStorage.setItem('oauthCodeTimestamp', new Date().toISOString());
      
      // Verificar si ya hemos procesado este código para evitar bucles
      const processedCodes = JSON.parse(sessionStorage.getItem('processedOAuthCodes') || '[]');
      if (processedCodes.includes(code)) {
        console.warn('[TiendanubeRedirect] Este código OAuth ya fue procesado anteriormente. Posible bucle detectado.');
        setErrorMessage('Se detectó un posible bucle de redirección. Por favor, inicie sesión nuevamente desde el principio.');
        setRedirecting(false);
        
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
        return;
      }
      
      // Registrar este código como procesado para evitar reprocesarlo
      processedCodes.push(code);
      sessionStorage.setItem('processedOAuthCodes', JSON.stringify(processedCodes));
      
      // IMPORTANTE: Verificamos si estamos en la ruta correcta del frontend o si deberíamos estar en el backend
      // La ruta /api/auth/callback en el frontend debe redirigir al backend
      // Para evitar bucles infinitos, verificamos explícitamente el pathname
      
      if (window.location.pathname === '/api/auth/callback') {
        console.log('[TiendanubeRedirect] Estamos en la ruta frontend que debe ser manejada por el backend.');
        
        // Verificar si ya hemos redirigido muchas veces
        const redirectCount = parseInt(sessionStorage.getItem('apiCallbackRedirectCount') || '0');
        if (redirectCount > 3) {
          console.error('[TiendanubeRedirect] Demasiadas redirecciones a /api/auth/callback. Deteniendo bucle.');
          setErrorMessage('Ha ocurrido un problema técnico con la redirección. Por favor, inténtelo más tarde o contacte a soporte.');
          setRedirecting(false);
          return;
        }
        
        // Incrementar contador de redirecciones
        sessionStorage.setItem('apiCallbackRedirectCount', (redirectCount + 1).toString());
        
        try {
          // En desarrollo local, redireccionar a la función cloud
          // En producción, usamos la URL absoluta para evitar problemas con reglas de reescritura
          if (process.env.NODE_ENV === 'development') {
            const localBackendUrl = `http://localhost:5001/nubelytics/us-central1/api/auth/callback?code=${code}&state=${state || ''}`;            
            console.log(`[TiendanubeRedirect] Ambiente de desarrollo. Redirigiendo a: ${localBackendUrl}`);
            sessionStorage.setItem('backendRedirectUrl', localBackendUrl);
            
            // Usar método que no causa bucle
            setStatusMessage('Redirigiendo a la API...');
            setTimeout(() => {
              window.location.href = localBackendUrl;
            }, 1000);
          } else {
            // En producción, usamos URL absoluta y fetch para evitar problemas con reescrituras
            console.log('[TiendanubeRedirect] Ambiente de producción. Manejando código OAuth sin recarga');
            sessionStorage.setItem('productionOAuthCodeHandling', 'direct');
            
            // Nubelytics.com, URL absoluta de backend de Firebase Functions
            const backendUrl = `https://nubelytics.com/api/auth/callback?code=${code}&state=${state || ''}`;
            setStatusMessage('Procesando autenticación con Tiendanube...');
            
            // Redirección directa sin recarga
            setTimeout(() => {
              window.location.href = backendUrl;
            }, 1000);
          }
        } catch (err) {
          console.error('[TiendanubeRedirect] Error al redirigir:', err);
          sessionStorage.setItem('redirectError', err.message || err.toString());
          sessionStorage.setItem('redirectErrorTime', new Date().toISOString());
          
          setErrorMessage(`Error al procesar la redirección: ${err.message || err}`);
          setRedirecting(false);
          
          // En caso de error, intentamos volver al inicio
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 3000);
        }
      } else if (window.location.pathname === '/auth/callback') {
        // Esta es la ruta del frontend que maneja el token devuelto por el backend
        console.log('[TiendanubeRedirect] Estamos en /auth/callback, esperando token del backend...');
        
        // Verificar si hay token en la URL
        const token = searchParams.get('token');
        if (token) {
          console.log('[TiendanubeRedirect] Token recibido del backend, dejando que Login lo procese');
          sessionStorage.setItem('tokenReceived', 'true');
          sessionStorage.setItem('tokenReceivedTime', new Date().toISOString());
          // No hacemos nada, dejamos que el componente Login maneje el token
        } else {
          console.warn('[TiendanubeRedirect] No se encontró token en /auth/callback');
          setErrorMessage('No se recibió el token de autenticación. Por favor, intente nuevamente.');
          setRedirecting(false);
          
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
        }
      } else {
        // Si estamos en otra ruta con código de autorización (caso raro)
        console.warn(`[TiendanubeRedirect] Código de autorización detectado en ruta inesperada: ${window.location.pathname}`);
        sessionStorage.setItem('unexpectedPathWithCode', window.location.pathname);
        
        // Redirigir al login para reiniciar el proceso
        setErrorMessage('Error en el proceso de autenticación. Redirigiendo al inicio...');
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
      }
    } else {
      // Si no hay código, verificar si venimos del login de Tiendanube
      if (isFromTiendanubeLogin) {
        console.log('[TiendanubeRedirect] Detectado retorno desde login de Tiendanube sin código');
        setStatusMessage('Sesión iniciada en Tiendanube. Reiniciando proceso de autorización...');
        
        // Almacenar que el usuario ya se autenticó en Tiendanube para referencia
        sessionStorage.setItem('tiendanubeLoginCompleted', 'true');
        sessionStorage.setItem('tiendanubeLoginTime', new Date().toISOString());
        
        // Contar cuántas veces hemos reiniciado el flujo OAuth para evitar bucles
        const restartCount = parseInt(sessionStorage.getItem('oauthRestartCount') || '0');
        if (restartCount >= 2) {
          console.error('[TiendanubeRedirect] Demasiados reintentos de flujo OAuth, posible problema con la cuenta');
          setErrorMessage('Ha ocurrido un problema al conectar con Tiendanube. Por favor, intenta nuevamente más tarde.');
          setRedirecting(false);
          
          // Ofrecer botones manuales en lugar de redirección automática
          return;
        }
        
        // Incrementar contador de reinicios
        sessionStorage.setItem('oauthRestartCount', (restartCount + 1).toString());
        
        // Usar URL absoluta para iniciar el flujo OAuth
        const isLocal = window.location.hostname === 'localhost';
        const loginUrl = isLocal
          ? 'http://localhost:5001/nubelytics/us-central1/api/auth/login'
          : 'https://nubelytics.com/api/auth/login';
        
        // Volver a iniciar el flujo OAuth después de autenticarse en Tiendanube
        setTimeout(() => {
          console.log('[TiendanubeRedirect] Reiniciando flujo OAuth después del login en Tiendanube');
          window.location.href = loginUrl;
        }, 2000);
      } else {
        // Caso normal: no hay código y no venimos del login de Tiendanube
        console.warn('[TiendanubeRedirect] No se encontró código de autorización en los parámetros');
        setErrorMessage('No se recibió un código de autorización válido. Redirigiendo al inicio de sesión...');
        setRedirecting(false);
        
        // Redireccionar después de mostrar el mensaje
        setTimeout(() => {
          console.log('[TiendanubeRedirect] Redirigiendo a login después del error...');
          navigate('/login', { replace: true });
        }, 3000);
      }
    }
  }, [searchParams]);

  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>Procesando autenticación...</h2>
      {errorMessage ? (
        <div style={{ marginTop: '1rem', padding: '1rem', background: '#fff3cd', color: '#664d03', borderRadius: '4px', border: '1px solid #ffecb5' }}>
          <p>{errorMessage}</p>
        </div>
      ) : statusMessage ? (
        <div style={{ marginTop: '1rem', padding: '1rem', background: '#d1e7dd', color: '#0f5132', borderRadius: '4px', border: '1px solid #badbcc' }}>
          <p>{statusMessage}</p>
        </div>
      ) : (
        <p>Por favor, espere mientras completamos el proceso de autorización de Tiendanube.</p>
      )}
      
      {redirecting && (
        <div style={{ marginTop: '2rem' }}>
          <div style={{ width: '50px', height: '50px', border: '5px solid #f3f3f3', 
                      borderTop: '5px solid #3498db', borderRadius: '50%', 
                      margin: '0 auto', animation: 'spin 2s linear infinite' }} />
        </div>
      )}
      
      <div style={{ marginTop: '2rem', fontSize: '0.8rem', color: '#6c757d' }}>
        <p>Si la redirección no ocurre automáticamente:</p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
          <button 
            onClick={() => window.location.href = '/api/auth/login'}
            style={{ padding: '8px 16px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Reiniciar autenticación
          </button>
          <button 
            onClick={() => window.location.href = '/'}
            style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Volver al inicio
          </button>
        </div>
      </div>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default TiendanubeRedirect;
